import React, { useRef, useState, useEffect } from 'react';
import { ZoomInIcon, ZoomOutIcon, Refresh } from "../../components/Icons"
import styles from './style.module.scss';
import { getImagePath } from '../../getImagePath';

const MAP_INITIAL_POSITION = { x: 6.60309, y: -7.12249 };
const MOBILE_MAP_INITIAL_POSITION = { x: -0.638, y: -6.972 };
const MAP_SCALE = 1.8;
const MOBILE_MAP_SCALE = 0.9;
const COLOR_ORDINAL = [
    "var(--color-purple-a)",
    "var(--color-purple-b)",
    "var(--color-purple-c)",
    "var(--color-purple-d)",
    "#ffcfe4",
    "#D2D2D2",
];
const DESKTOP_SCALE_STEP = 0.1;
const MOBILE_SCALE_STEP = 0.2;

const WORDING = {
    title: "調查範圍",
    description:
        "花蓮縣及臺東縣各鄉鎮市區震度達 4 級以上且震度面積超過該鄉鎮面積 50% 以上者為調查範圍，共計有 24 個鄉鎮，如下圖、表所示。",
    level: "震度",
    levels: ["六強", "六弱", "五強", "五弱", "四級", "三級以下"],
    img: getImagePath("/homepage/level-investigation_zh.png"),
};

const MOBILE_BREAKPOINT = 1028;

function MapChart() {
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    const STEP_SCALE = isMobile ? MOBILE_SCALE_STEP : DESKTOP_SCALE_STEP;
    const mapRef = useRef(null);
    const [scale, setScale] = useState(window.innerWidth > MOBILE_BREAKPOINT ? MAP_SCALE : MOBILE_MAP_SCALE);
    const [mapPosition, setMapPosition] = useState(window.innerWidth > MOBILE_BREAKPOINT ? MAP_INITIAL_POSITION : MOBILE_MAP_INITIAL_POSITION);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState(window.innerWidth > MOBILE_BREAKPOINT ? MAP_INITIAL_POSITION : MOBILE_MAP_INITIAL_POSITION);

    const mapChart = WORDING;

    const handleMouseDown = (e) => {
        if (e.touches) {
            e.preventDefault();
        }
        setIsDragging(true);
        const rect = mapRef.current.parentElement.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        setDragStart({
            x: clientX - (mapPosition.x * rect.width) / 100,
            y: clientY - (mapPosition.y * rect.height) / 100,
        });
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        if (e.touches) {
            e.preventDefault();
        }

        const rect = mapRef.current.parentElement.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const deltaX = ((clientX - dragStart.x) / rect.width) * 100;
        const deltaY = ((clientY - dragStart.y) / rect.height) * 100;

        setMapPosition({
            x: deltaX,
            y: deltaY,
        });
    };

    const handleMouseUp = (e) => {
        if (e.touches) {
            e.preventDefault();
        }
        setIsDragging(false);
    };

    useEffect(() => {
        const element = mapRef.current;
        if (!element) return;

        const touchStartHandler = (e) => {
            e.preventDefault();
            handleMouseDown(e);
        };

        const touchMoveHandler = (e) => {
            e.preventDefault();
            handleMouseMove(e);
        };

        const touchEndHandler = (e) => {
            e.preventDefault();
            handleMouseUp(e);
        };

        // 添加觸控事件監聽，使用 passive: false 來允許 preventDefault
        element.addEventListener('touchstart', touchStartHandler, { passive: false });
        element.addEventListener('touchmove', touchMoveHandler, { passive: false });
        element.addEventListener('touchend', touchEndHandler, { passive: false });

        // 清理函數
        return () => {
            element.removeEventListener('touchstart', touchStartHandler);
            element.removeEventListener('touchmove', touchMoveHandler);
            element.removeEventListener('touchend', touchEndHandler);
        };
    }, [isDragging, dragStart, mapPosition]);

    const resetAll = () => {
        setScale(window.innerWidth > MOBILE_BREAKPOINT ? 1 : MOBILE_MAP_SCALE);
        setMapPosition(window.innerWidth > MOBILE_BREAKPOINT ? MAP_INITIAL_POSITION : MOBILE_MAP_INITIAL_POSITION);
    };

    const LEVELS = mapChart.levels;
    const LEVEL_TITLE = mapChart.level;
    return (
        <div className={styles.container}>
            <h4>{mapChart.title}</h4>
            <p dangerouslySetInnerHTML={{ __html: mapChart.description }} />
            <div className={styles.mapWrapper}>
                <div className={styles.legend}>
                    <img
                        src={getImagePath("homepage/full-tw-map.jpg")}
                        alt="full-tw-map"
                    />
                    <div className={styles.legendItems}>
                        <h5>{LEVEL_TITLE}</h5>
                        {LEVELS.map((level, index) => (
                            <div className={styles.legendItem} key={index}>
                                <span
                                    style={{
                                        backgroundColor: COLOR_ORDINAL[index],
                                    }}
                                />
                                <p>{level}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <div
                    className={styles.mapContainer}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    <img
                        style={{
                            transform: `scale(${scale}) translate(${mapPosition.x}%, ${mapPosition.y}%)`,
                        }}
                        ref={mapRef}
                        src={mapChart.img}
                        alt={mapChart.title}
                        draggable="false"
                    />
                </div>
                <div className={styles.zoomButtons}>
                    <button onClick={resetAll}>
                        <Refresh />
                    </button>
                    <button onClick={() => setScale(scale - STEP_SCALE)}>
                        <ZoomInIcon />
                    </button>
                    <button onClick={() => setScale(scale + STEP_SCALE)}>
                        <ZoomOutIcon />
                    </button>
                </div>
            </div>
            <div className={styles.mobileLegend}>
                <h5>{LEVEL_TITLE}</h5>
                <div>
                    {LEVELS.map((level, index) => (
                        <div className={styles.legendItem} key={index}>
                            <span
                                style={{
                                    backgroundColor: COLOR_ORDINAL[index],
                                }}
                            />
                            <p>{level}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default MapChart;
